import { useState } from "react";
import { useTranslation } from "react-i18next";
import StatusBadge from "@/components/base/StatusBadge";
import { formatFullDate, daysUntil } from "@/lib/format";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import { downloadLicenseFile } from "@/lib/licenseFile";
import RenewalRequestModal from "@/pages/licenses/components/RenewalRequestModal";
import DevicePanel from "@/pages/licenses/components/DevicePanel";
import { useRenewalRequest } from "@/pages/licenses/hooks/useRenewalRequest";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

interface LicenseCardProps {
  license: License;
  onRefresh?: () => void;
}

export default function LicenseCard({ license, onRefresh }: LicenseCardProps) {
  const { t } = useTranslation();
  const { edition, statusMeta: buildStatusMeta, sourceMeta: buildSourceMeta } = useLicenseLabels();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; tone: "success" | "error" } | null>(null);
  const { submit: submitRenewal, submitting: renewSubmitting } = useRenewalRequest();

  const statusMeta = buildStatusMeta(license.status);
  const sourceMeta = buildSourceMeta(license.source);
  const days = license.expires_at ? daysUntil(license.expires_at) : null;
  const expiringSoon = license.status === "active" && days !== null && days >= 0 && days <= 30;
  const activationPct = license.max_activations
    ? Math.min(100, Math.round((license.activation_count / license.max_activations) * 100))
    : 0;

  const lastFour = license.license_key.slice(-4);
  const maskedKey = `LH-••••-••••-••••-${lastFour}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(license.license_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleDownload = () => {
    try {
      const fileName = downloadLicenseFile(license);
      setDownloaded(true);
      setFeedback({ text: t("licenses.downloadSuccess", { file: fileName }), tone: "success" });
      window.setTimeout(() => setDownloaded(false), 2000);
    } catch {
      setFeedback({ text: t("licenses.downloadFailed"), tone: "error" });
    }
  };

  const handleSubmitRenewal = async (note: string) => {
    try {
      await submitRenewal(license, note);
      setRenewOpen(false);
      setRenewed(true);
      setFeedback({ text: t("licenses.renewSuccess"), tone: "success" });
    } catch (err) {
      setRenewOpen(false);
      setFeedback({ text: err instanceof Error ? err.message : t("licenses.submitFailed"), tone: "error" });
    }
  };

  return (
    <article className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-key-2-line text-xl" />
          </span>
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">{license.product_name}</h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-700">
                {edition(license.edition)}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-computer-line" />
                </span>
                {t("licenses.seatUnit", { count: license.seat_count })}
              </span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${sourceMeta.className}`}
                title={
                  license.source === "redeem"
                    ? t("licenses.sourceTipRedeem")
                    : t("licenses.sourceTipApplication")
                }
              >
                <span className="flex h-3 w-3 items-center justify-center">
                  <i className={sourceMeta.icon} />
                </span>
                {sourceMeta.label}
              </span>
            </div>
          </div>
        </div>
        <StatusBadge meta={statusMeta} />
      </div>

      <div className="mt-4 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] text-foreground-500">{t("licenses.licenseKeyLabel")}</p>
          <p className="mt-0.5 truncate font-mono text-sm tracking-wider text-foreground-950">
            {revealed ? license.license_key : maskedKey}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setRevealed((value) => !value)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={revealed ? "ri-eye-off-line" : "ri-eye-line"} />
            </span>
            {revealed ? t("licenses.hide") : t("licenses.show")}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
            </span>
            {copied ? t("licenses.copied") : t("licenses.copyKey")}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setDevicesOpen((value) => !value)}
          aria-expanded={devicesOpen}
          className="w-full cursor-pointer rounded-md border border-background-200 bg-background-100 px-4 py-3 text-left transition-colors hover:border-primary-300"
        >
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-foreground-600">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-device-line" />
              </span>
              {t("licenses.deviceDetail")}
            </span>
            <span className="flex items-center gap-1 font-medium text-foreground-950">
              {license.activation_count} / {license.max_activations}
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className={devicesOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </span>
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${activationPct}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-foreground-400">{t("licenses.deviceDetailHint")}</p>
        </button>
        {devicesOpen && <DevicePanel license={license} onRefresh={onRefresh} onClose={() => setDevicesOpen(false)} />}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-background-200 pt-4 text-xs">
        <span className="flex items-center gap-1.5 text-foreground-600">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
            <i className="ri-calendar-check-line" />
          </span>
          {t("licenses.issuedAt", { date: license.issued_at ? formatFullDate(license.issued_at) : "--" })}
        </span>
        <span className="flex items-center gap-1.5 text-foreground-600">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
            <i className="ri-time-line" />
          </span>
          {t("licenses.expiresAt", {
            date: license.expires_at ? formatFullDate(license.expires_at) : t("licenses.permanent"),
          })}
        </span>
        {expiringSoon && (
          <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 font-medium text-accent-800">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-alarm-warning-line" />
            </span>
            {t("licenses.expiringOnlyDays", { days })}
          </span>
        )}
        {license.status === "expired" && (
          <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-close-circle-line" />
            </span>
            {t("licenses.expiredRenew")}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        <p
          className={`min-w-0 flex-1 truncate text-xs ${
            feedback?.tone === "error" ? "text-accent-700" : "text-foreground-500"
          }`}
        >
          {feedback ? feedback.text : t("licenses.cardHint")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={downloaded ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {downloaded ? t("licenses.downloaded") : t("licenses.downloadFile")}
          </button>
          {license.status !== "revoked" && (
            <button
              type="button"
              disabled={renewed || renewSubmitting}
              onClick={() => setRenewOpen(true)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className={renewed ? "ri-check-line" : "ri-refresh-line"} />
              </span>
              {renewed ? t("licenses.renewSubmitted") : t("licenses.renew")}
            </button>
          )}
        </div>
      </div>

      <RenewalRequestModal
        open={renewOpen}
        license={license}
        busy={renewSubmitting}
        onConfirm={(note) => {
          void handleSubmitRenewal(note);
        }}
        onCancel={() => setRenewOpen(false)}
      />
    </article>
  );
}